'use client';

import React, { useEffect, useRef } from 'react';
import { cn } from '@/utils';

// Number of bars rendered in the meter. Bars flex to fill the available
// width, so this controls visual density rather than absolute size.
const BAR_COUNT = 24;
// Resting scale applied to every bar when there is no signal — keeps a thin
// baseline visible so the channel reads as "present but quiet".
const IDLE_SCALE = 0.12;

export interface AudioChannelMeterProps {
  /**
   * Web Audio analyser tapped off this channel's audio. When null the meter
   * renders a flat idle baseline (e.g. before the session is connected).
   */
  analyser: AnalyserNode | null;
  /**
   * Tailwind background-color class applied to the bars and the label dot,
   * used to visually distinguish input from output (e.g. `bg-brand-primary`).
   */
  barColorClassName: string;
  /**
   * Optional className for the outer wrapper.
   */
  className?: string;
  /**
   * Channel label shown above the bars (e.g. "You" or the persona name).
   */
  label: string;
}

/**
 * A compact, amplitude-reactive audio meter for a single channel (input or
 * output). Bars are driven directly from the analyser's frequency data on each
 * animation frame via DOM writes, avoiding React re-renders for smooth, low-
 * overhead motion. Used in the Voice Live audio-only displays to let users
 * distinguish their own speech (input) from the avatar's speech (output).
 */
export const AudioChannelMeter: React.FC<AudioChannelMeterProps> = ({
  analyser,
  barColorClassName,
  className,
  label,
}) => {
  const barRefs = useRef<Array<HTMLDivElement | null>>([]);

  useEffect(() => {
    const bars = barRefs.current;

    // No analyser yet — show a static idle baseline and run no loop.
    if (!analyser) {
      bars.forEach((bar) => {
        if (bar) bar.style.transform = `scaleY(${IDLE_SCALE})`;
      });
      return;
    }

    const data = new Uint8Array(analyser.frequencyBinCount);
    // Spread the bars across the lower frequency bins where speech energy
    // concentrates, so the meter reacts strongly to voice.
    const usableBins = Math.max(1, Math.floor(data.length * 0.7));
    const binsPerBar = Math.max(1, Math.floor(usableBins / bars.length));
    let rafId = 0;

    const render = () => {
      analyser.getByteFrequencyData(data);
      for (let i = 0; i < bars.length; i++) {
        const bar = bars[i];
        if (!bar) continue;
        let sum = 0;
        for (let j = 0; j < binsPerBar; j++) {
          sum += data[i * binsPerBar + j] || 0;
        }
        const avg = sum / binsPerBar / 255; // normalise to 0..1
        // Boost so normal speech fills most of the bar, then clamp.
        const scale = Math.min(1, IDLE_SCALE + avg * 1.8);
        bar.style.transform = `scaleY(${scale})`;
      }
      rafId = requestAnimationFrame(render);
    };
    rafId = requestAnimationFrame(render);

    return () => cancelAnimationFrame(rafId);
  }, [analyser]);

  return (
    <div
      aria-label={`${label} audio level`}
      className={cn('flex w-full flex-col gap-1', className)}
      role="img"
    >
      <span className="flex items-center gap-1.5 text-[10px] font-bold text-neutral-gray-600">
        <span className={cn('h-1.5 w-1.5 shrink-0 rounded-full', barColorClassName)} />
        {label}
      </span>
      <div className="flex h-7 items-center justify-center gap-0.5 sm:h-10">
        {Array.from({ length: BAR_COUNT }).map((_, i) => (
          <div
            key={i}
            ref={(el) => {
              barRefs.current[i] = el;
            }}
            className={cn('h-full flex-1 origin-center rounded-full', barColorClassName)}
            style={{ transform: `scaleY(${IDLE_SCALE})` }}
          />
        ))}
      </div>
    </div>
  );
};

export default AudioChannelMeter;
